import React from "react";

import Fetcher from "../common/Fetcher";
import PrestaGlobals from "../common/PrestaGlobals";
import Catalog from "./Catalog";
import ICatalogItem from "./ICatalogItem";

import { DatabaseContext } from "../database/DatabaseHandler";
import { CartContext } from "../cart/CartHandler";

/** Context for handling instant catalog data */
export const CatalogContext = React.createContext<{
    catalog: Catalog | null;
    initialExpandedIds: Set<number>;
    selectItem: (item: ICatalogItem) => void;
}>({
    catalog: null,
    initialExpandedIds: new Set(),
    selectItem: () => {}
});

/**
 * Handles the instant catalog functionality
 */
const CatalogHandler: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
    const dataContext = React.useContext(DatabaseContext);
    const cartContext = React.useContext(CartContext);

    // Use local state to hold data and updater
    const [catalog, setCatalog] = React.useState<Catalog | null>(null);

    const initialExpandedIds = React.useMemo<Set<number>>(() => {
        if (!catalog || !PrestaGlobals.productId) return new Set();
        const path = catalog.findCategoryPathByProductId(PrestaGlobals.productId);
        return new Set(path);
    }, [catalog]);

    // Update context value using useMemo to avoid unnecessary renders
    const catalogContextValue = React.useMemo(() => ({
        catalog,
        initialExpandedIds,
        selectItem: (item: ICatalogItem) => {
            if (item.ID === undefined) {
                console.error("Selected item has no ID, cannot update cookie.");
                return;
            }
            // Update product cookie with the selected product ID
            const f: Fetcher = new Fetcher(PrestaGlobals.virtualUri + "/cookie?ajax=1", "POST");
            f.request("product_id=" + item.ID, (response: Object | null) => {
                // Set new product info and reload scripts from the database
                PrestaGlobals.productName = item.Name;
                PrestaGlobals.productId = item.ID!;
                // The product's own scripts: a script design (a style override) would stay over them
                cartContext.setDesignStyle(null);
                cartContext.setAdminDesign(false);
                dataContext.reloadScripts();
            });
        }
    }), [catalog, initialExpandedIds]);

    // Fetch data on component mount
    React.useEffect(() => {
        const catalog: Catalog = new Catalog("/catalog?version=2");

        console.log('Loading catalog items...');
        Promise.all([
            catalog.request()
        ])
            .then(([catalogMsg]) => {
                setCatalog(catalog);
            })
            .catch((error) => {
                console.error('Error loading data:', error);
            });
    }, []);

    return (
        <CatalogContext.Provider value={catalogContextValue}>
            {children}
        </CatalogContext.Provider>
    );
};

export default CatalogHandler;
